/*
 * Copyright (C) 2025 JPMorgan Chase & Company
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, is not permitted.
 */

import {ExdsTextInput} from "@exds/controls";
import {useEffect, useState} from "react";
import {ExdsButton, ExdsLayout, ExdsSpacing, ExdsText} from "@exds/core";
import './PasswordInput.scss';
import { TuxStepperBars  } from '@tuxedo/react-component-library';
import {ExdsIconDangerFilled, ExdsIconLike} from "@exds/icons";

export default function PasswordInput(props) {
  const {
    id,
    label,
    value,
    onChange,
    errorText,
    required,
    onBlur,
    onClick,
    allRequirementsMet,
    showStepperData,
    showCharCount,
    validationGroup,
    className
  } = props;
  const [showInputValue, setShowInputValue] = useState(false);
  const charCount = value?.length;
  const weakPassword = value && !allRequirementsMet;
  const inactiveSuccessMeter = ['not started', 'not started', 'not started'];
  const weakSuccessMeter = ['active', 'not started', 'not started'];
  const strongSuccessMeter = ['active', 'active', 'active'];

  const [meterData, setMeterData] = useState(inactiveSuccessMeter);

  useEffect(() => {
    if (allRequirementsMet) setMeterData(strongSuccessMeter);
    else if (value) setMeterData(weakSuccessMeter);
    else setMeterData(inactiveSuccessMeter);
  }, [value, allRequirementsMet]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`password-input-container ${className || ''}`}>
      <ExdsTextInput
        id={id}
        value={value}
        onChange={onChange}
        labelText={label}
        errorText={errorText}
        required={required}
        type = {showInputValue ? 'text' : 'password'}
        validationGroup={validationGroup}
        trailingButton={
          <ExdsButton
            type="button"
            variant="link"
            onClick={() => setShowInputValue(!showInputValue)}
          >
            {showInputValue ? 'Hide' : 'Show'}
          </ExdsButton>
        }
        onBlur={onBlur}
        className="mt-0"
        onClick={onClick}
      />

      {(showStepperData || showCharCount) &&
        <>
          <ExdsSpacing margin={{top: '0.6rem'}} >
            <ExdsLayout
              display="flex"
              flexDirection="row"
              justifyContent={showStepperData ? 'space-between' : 'flex-end'}
              alignItems="flex-start"
            >
              {showStepperData &&
                <div className="stepper-width">
                  <TuxStepperBars data={meterData} className={`stepper ${allRequirementsMet ? 'strong' : value ? 'weak' : ''}`}/>
                </div>
              }
              <ExdsText textStyle="bodyMedium" className="char-count-text">
                {charCount} characters
              </ExdsText>
            </ExdsLayout>
          </ExdsSpacing>

          {showStepperData && value &&
            <ExdsSpacing margin={{top: '-0.5rem'}}>
              <ExdsLayout gap="0.25rem">
                { weakPassword ?
                  <ExdsIconDangerFilled color="error" size="medium" aria-label="Weak password" /> :
                  <ExdsIconLike color="success" size="medium" aria-label="Strong password" />
                }
                <ExdsText
                  textStyle="bodyMedium"
                  color={weakPassword ? 'error' : 'success'}
                >
                  {`${weakPassword ? 'Weak' : 'Strong'} password`}
                </ExdsText>
              </ExdsLayout>
            </ExdsSpacing>
          }
        </>
      }
    </div>
  );
}
